import React, { ComponentProps, MouseEvent, PropsWithChildren, useEffect } from "react";
import { Icon } from "@clarkinc/zest-design-system/ui";
import { SuperSimpleCarousel } from "@wss/carousel";
import { cls } from "@wss/cls";
import { CarouselItem } from "~/ProductDetails/Carousel/CarouselItem";
import { StarsCard } from "~/ProductDetails/Cards/StarsCard";
import { useHomePageData } from "../utilities";
import { useRecommendationCarouselItems } from "./hooks";
import { User, withUserAndChat } from "~/common/Store/connectUserAndChat";
import ChildOrNull from "~/common/ChildOrNull";
import { pushCarouselEventToDataLayer, sendProductImpressionEvent, setCarouselInteraction, trackProductClick } from "~/userAnalytics";
import { impressionTrackedItemNumbers } from "../../Gtm/Tracking/impressionTrackedItemNumbers";
import { useFeatureFlag } from "~/common/contexts/FeatureFlagProvider";
const carouselTypes = {
  RecommendedProductsForYou: "Recommended Products For You",
  BestSelling: "Best Selling Products"
} as const;
type CarouselTypes = typeof carouselTypes;
type HomePageCarouselItemProps = {
  product: CarouselItem;
} & Partial<ComponentProps<typeof StarsCard>>;
function trackCarouselProductClick(e: MouseEvent<HTMLUListElement>, carouselId: string) {
  const {
    id
  } = e.currentTarget.dataset;
  if (!id) {
    throw new Error(`The ${carouselId} carousel item is missing the data-id prop`);
  }
  setCarouselInteraction(id, carouselId);
  trackProductClick(id);
}
export function HomePageCarouselItem({
  product,
  ...rest
}: HomePageCarouselItemProps) {
  return <StarsCard {...rest} as={SuperSimpleCarousel.Item} data-id={product.itemNumber} className="w-32 shrink-0 p-4 first:border-l-0 border-0 border-l border-gray-200 border-solid last:border-r last:-mr-px overflow-hidden" anchorProps={{
    ...rest.anchorProps,
    href: product.link,
    title: product.description
  }} imgProps={{
    ...rest.imgProps,
    src: product.imagePath,
    alt: product.altText
  }} starsProps={{
    ...rest.starsProps,
    rating: product.overallRating
  }} descriptionProps={{
    ...rest.descriptionProps,
    children: product.description
  }} saleBadgeAndPricing={{
    ...rest.saleBadgeAndPricing,
    price: product.displaySalePrice ? product.salePrice : product.price
  }} priceProps={{
    ...rest.priceProps,
    price: product.displaySalePrice ? product.salePrice : product.price,
    unitOfMeasure: product.unitOfMeasure
  }} withPlusLink={product.isWebstaurantPlusEligible} withSalePrice={product.displaySalePrice} data-sentry-element="StarsCard" data-sentry-component="HomePageCarouselItem" data-sentry-source-file="index.tsx" />;
}
type RecommendedProductsForYouCarouselProps = {
  products: Readonly<CarouselItem[]>;
  carouselModel: string;
};
export function RecommendedProductsForYouCarousel({
  products,
  carouselModel
}: RecommendedProductsForYouCarouselProps) {
  return <section id="recommend-for-you" // Used by c.io, check with Design before updating / removing.
  data-testid="recommend-for-you" data-gtm-component="carousel" className="mb-4" data-placement-id={carouselModel} data-feature-name="Homepage Recommended Products" aria-label="Products Carousel" data-sentry-component="RecommendedProductsForYouCarousel" data-sentry-source-file="index.tsx">
      <HomePageCarousel data-sentry-element="HomePageCarousel" data-sentry-source-file="index.tsx">
        <TrackCarousel carouselName="Recommended For You" carouselModel={carouselModel} data-sentry-element="TrackCarousel" data-sentry-source-file="index.tsx">
          <TrackChildren data-sentry-element="TrackChildren" data-sentry-source-file="index.tsx">
            {products.map(product => <HomePageCarouselItem key={product.itemNumber} product={product} onClick={(e: MouseEvent<HTMLUListElement>) => trackCarouselProductClick(e, carouselModel)} />)}
          </TrackChildren>
        </TrackCarousel>
      </HomePageCarousel>
    </section>;
}
type BestSellingCarouselProps = {
  products: Readonly<CarouselItem[]>;
};
export function BestSellingCarousel({
  products
}: BestSellingCarouselProps) {
  return <section data-testid="best-selling" data-gtm-component="carousel" className="pb-0" data-placement-id="best_selling" data-feature-name="Homepage Best Selling" data-sentry-component="BestSellingCarousel" data-sentry-source-file="index.tsx">
      <HomePageCarousel data-sentry-element="HomePageCarousel" data-sentry-source-file="index.tsx">
        <TrackCarousel carouselName="Best Selling Products" carouselModel="best-selling" data-sentry-element="TrackCarousel" data-sentry-source-file="index.tsx">
          <TrackChildren data-sentry-element="TrackChildren" data-sentry-source-file="index.tsx">
            {products.map(product => <HomePageCarouselItem key={product.itemNumber} product={product} onClick={(e: MouseEvent<HTMLUListElement>) => trackCarouselProductClick(e, "best_selling")} />)}
          </TrackChildren>
        </TrackCarousel>
      </HomePageCarousel>
    </section>;
}
type RenderCarouselSectionsProps = {
  user: User;
};
function RenderCarouselSections({
  user,
  children
}: PropsWithChildren<RenderCarouselSectionsProps>) {
  const {
    evaluateFlag
  } = useFeatureFlag();
  const {
    bestSellingProductsCarousel: {
      carouselMembers: bestSellingProducts
    }
  } = useHomePageData();
  const [recommendedProductsForYou] = useRecommendationCarouselItems(user);
  let firstCarouselType: CarouselTypes[keyof CarouselTypes] | undefined;
  let secondCarouselType: CarouselTypes[keyof CarouselTypes] | undefined;
  if (recommendedProductsForYou?.recAiCarouselItems?.length) {
    firstCarouselType = carouselTypes.RecommendedProductsForYou;
  }
  if (bestSellingProducts.length) {
    secondCarouselType = carouselTypes.BestSelling;
  }
  return <>
      <ChildOrNull condition={!!firstCarouselType} data-sentry-element="ChildOrNull" data-sentry-source-file="index.tsx">
        <div className={`container pt-6 ${user.isLoggedIn ? "bg-gray-100" : ""}`}>
          {!evaluateFlag("personalized-product-recommendations") && firstCarouselType === carouselTypes.RecommendedProductsForYou ? <RecommendedProductsForYouCarousel products={recommendedProductsForYou?.recAiCarouselItems || []} carouselModel={recommendedProductsForYou?.eventLabel || ""} /> : null}
        </div>
      </ChildOrNull>
      {children}
      <ChildOrNull condition={!!secondCarouselType} data-sentry-element="ChildOrNull" data-sentry-source-file="index.tsx">
        <div className="bg-white">
          <div className="container">
            <h2 className="text-center text-gray-800 text-2xl md:text-3xl mb-4">
              {secondCarouselType}
            </h2>
            {secondCarouselType === carouselTypes.BestSelling ? <BestSellingCarousel products={bestSellingProducts} /> : null}
          </div>
        </div>
      </ChildOrNull>
    </>;
}
export const CarouselSections = withUserAndChat(RenderCarouselSections);
export function FeaturedCarousel() {
  const {
    carouselMembers
  } = useHomePageData().carousel;
  return <HomePageCarousel className="rounded-l-none" previousProps={{
    className: "rounded-l-none"
  }} data-sentry-element="HomePageCarousel" data-sentry-component="FeaturedCarousel" data-sentry-source-file="index.tsx">
      <TrackCarousel carouselName="Featured Carousel" carouselModel="Manual" data-sentry-element="TrackCarousel" data-sentry-source-file="index.tsx">
        <TrackChildren data-sentry-element="TrackChildren" data-sentry-source-file="index.tsx">
          {carouselMembers.map(product => <HomePageCarouselItem key={product.itemNumber} product={product} onClick={(e: MouseEvent<HTMLUListElement>) => trackCarouselProductClick(e, "featured-carousel")} />)}
        </TrackChildren>
      </TrackCarousel>
    </HomePageCarousel>;
}
function TrackChildren({
  children
}: PropsWithChildren) {
  const {
    carouselRef
  } = SuperSimpleCarousel.useContext();
  useEffect(function initGtmCarouselItemTracking() {
    if (!carouselRef.current) return;
    function onObserve(entries: IntersectionObserverEntry[]) {
      const ids: {
        itemNumber: string;
      }[] = [];
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (!(entry.target instanceof HTMLElement)) continue;
        if (!entry.target.dataset.id) continue;
        ids.push({
          itemNumber: entry.target.dataset.id
        });
        observer.unobserve(entry.target);
      }
      sendProductImpressionEvent(ids);
    }
    const observer = new IntersectionObserver(onObserve, {
      threshold: [0.8]
    });
    for (const entry of carouselRef.current.children) {
      const itemNumber = entry?.getAttribute("data-id")?.toUpperCase() ?? "";
      if (impressionTrackedItemNumbers.has(itemNumber)) {
        observer.observe(entry);
      }
    }
    return function cleanupGtmCarouselItemTracking() {
      observer.disconnect();
    };
  }, [carouselRef]);
  return <>{children}</>;
}
function TrackCarousel({
  children,
  carouselName,
  carouselModel
}: PropsWithChildren<{
  carouselName: string;
  carouselModel: string;
}>) {
  const {
    carouselRef
  } = SuperSimpleCarousel.useContext();
  useEffect(function initGtmCarouselTracking() {
    if (!carouselRef.current) return;
    function onObserve(entries: IntersectionObserverEntry[]) {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        pushCarouselEventToDataLayer(carouselName, "", carouselModel);
        observer.disconnect();
      }
    }
    const observer = new IntersectionObserver(onObserve, {
      threshold: [0.8]
    });
    observer.observe(carouselRef.current);
    return function cleanupGtmCarouselTracking() {
      observer.disconnect();
    };
  }, [carouselName, carouselRef, carouselModel]);
  return <>{children}</>;
}
type HomePageCarouselProps = ComponentProps<typeof SuperSimpleCarousel> & {
  previousProps?: ComponentProps<typeof SuperSimpleCarousel.Previous>;
  contentProps?: ComponentProps<typeof SuperSimpleCarousel.Content>;
  nextProps?: ComponentProps<typeof SuperSimpleCarousel.Next>;
};
export function HomePageCarousel({
  children,
  className,
  previousProps,
  contentProps,
  nextProps,
  ...rest
}: PropsWithChildren<HomePageCarouselProps>) {
  return <SuperSimpleCarousel {...rest} className={cls("rounded border border-gray-200 border-solid bg-white w-full", className)} data-sentry-element="SuperSimpleCarousel" data-sentry-component="HomePageCarousel" data-sentry-source-file="index.tsx">
      <SuperSimpleCarousel.Previous {...previousProps} className={cls("hidden bg-white h-full border-none border-r border-black/5 shadow-[-10px_0_8px_10px] shadow-black/25 z-10 lt:block", previousProps?.className)} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
        <Icon name="ri-arrow-left-s-line" className="fill-green-500 w-6 h-6" data-sentry-element="Icon" data-sentry-source-file="index.tsx" />
      </SuperSimpleCarousel.Previous>
      <SuperSimpleCarousel.Content {...contentProps} className={cls("m-0 -mx-px lt:scroll-p-9", contentProps?.className)} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
        {children}
      </SuperSimpleCarousel.Content>
      <SuperSimpleCarousel.Next {...nextProps} className={cls("hidden bg-white border-l border-none h-full border-l-black/5 shadow-[10px_0_8px_10px] shadow-black/25 lt:block", nextProps?.className)} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
        <Icon name="ri-arrow-right-s-line" className="fill-green-500 w-6 h-6" data-sentry-element="Icon" data-sentry-source-file="index.tsx" />
      </SuperSimpleCarousel.Next>
    </SuperSimpleCarousel>;
}